import { useEffect, useMemo } from 'react';

import { nanoid } from '@reduxjs/toolkit';
import { addReaction, removeReaction } from 'services/reactions';

import useReactionStorage from './useReactionStorage';

export type Reaction = 'like';

export interface ReactionObject {
  addReaction: (callback) => void;
  removeReaction: (callback) => void;
}

export interface ReactionEventObject {
  event: 'reaction_change';
  uuid: string;
  type: string;
  slug: string;
  active: boolean;
  reaction: Reaction;
  onReactionEvent?: (event: MessageEvent<ReactionEventObject>) => void;
}

const REACTION_CHANGE_EVENT = 'reaction_change';

export default function useReaction(
  type: string,
  slug: string,
  reaction: Reaction,
  onReactionEvent?: (event: MessageEvent<ReactionEventObject>) => void
): ReactionObject {
  const uuid = useMemo(() => nanoid(), []);
  const { storage, addToStorage, removeFromStorage } = useReactionStorage();

  const addReactionPostMessage = () => {
    window.postMessage(
      { event: REACTION_CHANGE_EVENT, uuid, type, slug, reaction, active: true },
      '*'
    );
  };

  const removeReactionPostMessage = () => {
    window.postMessage(
      { event: REACTION_CHANGE_EVENT, uuid, type, slug, reaction, active: false },
      '*'
    );
  };

  useEffect(() => {
    if (storage) {
      if (storage.includes(slug)) {
        addReactionPostMessage();
      } else {
        removeReactionPostMessage();
      }
    }
  }, [storage]);

  const handleReactionChange = (event: MessageEvent<ReactionEventObject>) => {
    if (event.data?.uuid !== uuid) return;

    onReactionEvent?.(event);
  };

  useEffect(() => {
    window.addEventListener('message', (event: MessageEvent<ReactionEventObject>) => {
      if (event.data?.event === REACTION_CHANGE_EVENT) {
        handleReactionChange(event);
      }
    });

    return () => {
      window.removeEventListener('message', (event: MessageEvent<ReactionEventObject>) => {
        if (event.data?.event === REACTION_CHANGE_EVENT) {
          handleReactionChange(event);
        }
      });
    };
  }, []);

  const handleAddReaction = (callback) => {
    addToStorage(slug);
    addReactionPostMessage();
    addReaction(type, slug, reaction).then(() => callback?.());
  };

  const handleRemoveReaction = (callback) => {
    removeFromStorage(slug);
    removeReactionPostMessage();
    removeReaction(type, slug, reaction).then(() => callback?.());
  };

  return {
    addReaction: (callback) => handleAddReaction(callback),
    removeReaction: (callback) => handleRemoveReaction(callback),
  };
}
